/**
 * PathProgressView — pure presentational component.
 *
 * Renders a resolved path progress (from resolvePathProgress) as a list of
 * collapsible level groups with step rows. Used by both YourPath and
 * PathDashboard so the two surfaces stay visually consistent.
 *
 * Props:
 *   pathTitle  {string}  — shown as kicker context (optional)
 *   progress   {object}  — return value of resolvePathProgress()
 */
import React, { useState } from "react";
import styled from "styled-components";
import { Link } from "gatsby";

// ---------------------------------------------------------------------------
// Styled components (matching YourPath.js: Space Mono kickers, var(--nd-*))
// ---------------------------------------------------------------------------

const LevelGroup = styled.div`
  border: 1px solid var(--nd-border);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
`;

const LevelHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: var(--nd-surface);
  border: none;
  padding: 14px 20px;
  cursor: pointer;
  color: var(--nd-text-display);
  text-align: left;

  &:hover { background: var(--nd-border); }
`;

const LevelTitle = styled.span`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: var(--nd-text-display);
`;

const LevelMeta = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-left: 8px;
`;

const LevelRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const Chevron = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  color: var(--nd-text-disabled);
  transition: transform 200ms ease;
  transform: rotate(${({ open }) => (open ? "90deg" : "0deg")});
`;

const StepList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--nd-border);
`;

const StepRow = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  border-bottom: 1px solid var(--nd-border);

  &:last-child { border-bottom: none; }
`;

const StepMarker = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 14px;
  flex-shrink: 0;
  color: ${({ done }) => (done ? "var(--nd-success, #4ade80)" : "var(--nd-text-disabled)")};
`;

const StepLink = styled(Link)`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-primary);
  text-decoration: none;
  line-height: 1.4;
  flex-grow: 1;

  &:hover { color: var(--nd-text-display); text-decoration: underline; }
`;

const TypeTag = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 9px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  border: 1px solid var(--nd-border-visible);
  border-radius: 4px;
  padding: 2px 6px;
  flex-shrink: 0;
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deepLink(step) {
  switch (step.type) {
    case "lab":
      return "/lab";
    case "module":
      return `/academy/elearning/${step.refId}/`;
    case "cert":
      return "/career-path";
    default:
      return "#";
  }
}

// ---------------------------------------------------------------------------
// Collapsible level block
// ---------------------------------------------------------------------------

function LevelBlock({ level, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <LevelGroup>
      <LevelHeader
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div>
          <LevelTitle>{level.title}</LevelTitle>
          <LevelMeta>
            {level.doneCount}/{level.total}
          </LevelMeta>
        </div>
        <LevelRight>
          <LevelMeta>{level.pct}%</LevelMeta>
          <Chevron open={open}>&#9654;</Chevron>
        </LevelRight>
      </LevelHeader>

      {open && (
        <StepList>
          {level.steps.map((step) => (
            <StepRow key={`${step.type}:${step.refId}`}>
              <StepMarker done={step.done} aria-hidden="true">
                {step.done ? "✓" : "○"}
              </StepMarker>
              <StepLink to={deepLink(step)}>{step.title}</StepLink>
              <TypeTag>{step.type}</TypeTag>
            </StepRow>
          ))}
        </StepList>
      )}
    </LevelGroup>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

const PathProgressView = ({ pathTitle, progress }) => {
  if (!progress) return null;

  const firstIncompleteIdx = progress.levels.findIndex(
    (_, i) => !progress.completedLevelIndexes.includes(i),
  );

  return (
    <div aria-label={pathTitle ? `Path: ${pathTitle}` : "Learning path"}>
      {progress.levels.map((level, idx) => (
        <LevelBlock
          key={level.title}
          level={level}
          defaultOpen={idx === firstIncompleteIdx || (firstIncompleteIdx === -1 && idx === 0)}
        />
      ))}
    </div>
  );
};

export default PathProgressView;
